// src/modules/bookLocation/keyHandover/hooks/useSubscriptionUrlSync.ts

import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import { mergeQueries } from "../common/url/subscriptionQuery";

/**
 * Sync subscription Redux state to URL query parameters
 *
 * Updates URL when subscription state changes (without triggering navigation).
 * Only syncs after initial hydration to avoid overwriting URL on mount.
 *
 * @param hydratedRef - Ref tracking whether initial hydration has completed
 *
 * @remarks
 * - Handles both Flow 2 (new subscription) and Flow 3 (existing subscription)
 * - Preserves existing booking query parameters
 * - Uses replace navigation to avoid history pollution
 *
 * @example
 * ```tsx
 * function SubscriptionPage() {
 *   const hydratedRef = useRef(false);
 *   const subscriptionHydrated = useSubscriptionHydration();
 *
 *   useEffect(() => {
 *     if (subscriptionHydrated) hydratedRef.current = true;
 *   }, [subscriptionHydrated]);
 *
 *   useSubscriptionUrlSync(hydratedRef);
 *   return <Content />;
 * }
 * ```
 */
export function useSubscriptionUrlSync(hydratedRef: React.RefObject<boolean>): void {
    const location = useLocation();
    const navigate = useNavigate();

    const selectedLocation = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedLocation
    );
    const paymentModel = useSelector(
        (state: RootState) => state.bookingCreation.keyHandoverSelectedPaymentModel
    );
    const subscriptionFlow = useSelector(
        (state: RootState) => state.subscription.hasExistingSubscription
    );
    const newSubEmail = useSelector((state: RootState) => state.subscription.newSubscription.email);
    const newSubEmailVerified = useSelector(
        (state: RootState) => state.subscription.newSubscription.isEmailVerified
    );
    const newSubSelectedPlan = useSelector(
        (state: RootState) => state.subscription.newSubscription.selectedSubscriptionPlan
    );
    const newSubEligible = useSelector(
        (state: RootState) => state.subscription.newSubscription.isEligible
    );
    const newSubHostData = useSelector(
        (state: RootState) => state.subscription.newSubscription.hostData
    );
    const newSubPaid = useSelector(
        (state: RootState) => state.subscription.newSubscription.paymentCompleted
    );
    const newSubId = useSelector(
        (state: RootState) => state.subscription.newSubscription.subscriptionId
    );
    const existingSubEmail = useSelector(
        (state: RootState) => state.subscription.existingSubscription.email
    );
    const existingSubVerified = useSelector(
        (state: RootState) => state.subscription.existingSubscription.isVerified
    );

    useEffect(() => {
        // Only sync after initial hydration
        if (!hydratedRef.current) return;

        // Build subscription query from Redux state
        const subscriptionQuery = {
            locationId: selectedLocation?.id || undefined,
            paymentModel: paymentModel || undefined,
            subscriptionFlow: subscriptionFlow || undefined,

            // Flow 2: New subscription
            ...(subscriptionFlow === "new" && {
                email: newSubEmail || undefined,
                emailVerified: newSubEmailVerified ? ("true" as "true") : undefined,
                eligible:
                    newSubEligible !== null
                        ? newSubEligible
                            ? ("true" as "true")
                            : ("false" as "false")
                        : undefined,
                selectedSubscriptionPlanId: newSubSelectedPlan?.id || undefined,
                // Host data
                hostFirstName: newSubHostData?.firstName || undefined,
                hostLastName: newSubHostData?.lastName || undefined,
                hostEmail: newSubHostData?.email || undefined,
                hostPhone: newSubHostData?.phone || undefined,
                subscriptionPaid: newSubPaid ? ("true" as "true") : undefined,
                subscriptionId: newSubId || undefined,
            }),

            // Flow 3: Existing subscription
            ...(subscriptionFlow === "existing" && {
                email: existingSubEmail || undefined,
                subscriptionVerified: existingSubVerified ? ("true" as "true") : undefined,
            }),
        };

        // Merge with existing booking query
        const combinedQuery = mergeQueries(location.search, subscriptionQuery);

        // Only update URL if it changed
        const currentQuery = location.search.startsWith("?")
            ? location.search.substring(1)
            : location.search;

        if (combinedQuery !== currentQuery) {
            navigate(
                {
                    pathname: location.pathname,
                    search: `?${combinedQuery}`,
                },
                { replace: true } // Don't create new history entry
            );
        }
    }, [
        hydratedRef,
        selectedLocation,
        paymentModel,
        subscriptionFlow,
        newSubEmail,
        newSubEmailVerified,
        newSubSelectedPlan,
        newSubEligible,
        newSubHostData,
        newSubPaid,
        newSubId,
        existingSubEmail,
        existingSubVerified,
        location.pathname,
        location.search,
        navigate,
    ]);
}
